import { Tab } from '@headlessui/react'
import { Banner, LoadingWand } from '@ordr-as/umami-react'
import { useContext } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useGetPayment, useGetPaymentEvents } from '../../api/paymentApi'
import Container from '../../components/layout/Container'
import { SelectTabContext } from '../../context/SelectTabContext'
import DetailsTab from './detailsTab/DetailsTab'
import EventsTab from './eventsTab/EventsTab'
import config from '../../config'
import { ExternallinkIcon } from '@ordr-as/icons'
import { PaymentState } from '../../../generated-api'

const PaymentDetailPage = () => {
    const { selectedTabIndex, setSelectedTabIndex } = useContext(SelectTabContext)
    const { paymentReference } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const { data: payment, isLoading: isPaymentLoading, error: paymentError } = useGetPayment(paymentReference!)
    const {
        data: paymentEvents,
        isLoading: isPaymentEventsLoading,
        error: paymentEventError,
    } = useGetPaymentEvents(paymentReference!)
    const base = `/payments/${paymentReference}`
    const tabs = [
        {
            label: 'Details',
            path: '',
            content: <DetailsTab payment={payment!} />,
        },
        {
            label: 'Payment events',
            path: 'payment-events',
            content: <EventsTab events={paymentEvents} loading={isPaymentEventsLoading} error={paymentEventError} />,
        },
    ]

    const lastSegment = location.pathname.split('/').pop()
    const initialTabIndex = (function () {
        if (lastSegment === 'payment-events') return 1
        return 0
    })()

    const handleTabChange = (idx: number) => {
        setSelectedTabIndex(idx)
        navigate(tabs[idx].path === '' ? base : `${base}/${tabs[idx].path}`)
    }

    return (
        <>
            {isPaymentLoading ? (
                <div className="text-center flex h-screen">
                    <div className="m-auto">
                        <LoadingWand className="-mt-9 mx-auto" />
                    </div>
                </div>
            ) : payment ? (
                <Container tabletWidth="full" desktopWidth="full" maxWidth="7xl" marginX="0" returnUrl="/payments">
                    <div className="px-3">
                        <div className="flex items-center">
                            <h1 className="inline mr-3">Payment details</h1>
                            <span className="sr-only">{payment.latestState}</span>
                            <div
                                className={`rounded-full w-0 p-3 inline-block ${
                                    payment.latestState === PaymentState.CAPTURED
                                        ? 'bg-green-dark'
                                        : payment.latestState === PaymentState.FAILED
                                          ? 'bg-orange'
                                          : 'bg-yellow-light'
                                }`}
                            />
                        </div>
                        <p>{paymentReference}</p>
                        <div>
                            <a
                                href={`${config.adminBaseUrl}/#/location/${payment.executingLocationId}/order-details/${payment.orderReference}`}
                                target="_blank"
                                rel="noreferrer"
                                className="py-1 pr-2"
                            >
                                <span className="underline mr-2">See order in admin</span>
                                <ExternallinkIcon className="text-xs inline align-baseline" />
                            </a>
                        </div>
                        {payment?.pspReference && payment.paymentProvider === 'ADYEN' && (
                            <div>
                                <a
                                    href={`${config.adyen.caBaseUrl}/ca/ca/accounts/showTx.shtml?pspReference=${payment.pspReference}&txType=Payment`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="py-1 pr-2"
                                >
                                    <span className="underline mr-2">See payment in Adyen</span>
                                    <ExternallinkIcon className="text-xs inline align-baseline" />
                                </a>
                            </div>
                        )}
                        <Tab.Group key={selectedTabIndex} defaultIndex={initialTabIndex} onChange={handleTabChange}>
                            <Tab.List className="flex space-x-3 rounded-xl mt-4">
                                {tabs.map(({ label }, idx) => (
                                    <Tab
                                        key={idx}
                                        className={({ selected }) =>
                                            `w-1/3 tablet:w-1/4 desktop:w-1/5 py-4 leading-5 font-medium rounded-t-lg  shadow-lg z-10
                                    ${
                                        selected
                                            ? 'bg-brand-white text-brand-black w-200 border-b-4 border-brand-primary'
                                            : 'hover:bg-purple-lightest bg-grey-medium hover:text-brand-black text-grey-dark opacity-80 hover:opacity-100'
                                    }`
                                        }
                                    >
                                        {label}
                                    </Tab>
                                ))}
                            </Tab.List>
                            <Tab.Panels className="relative z-10">
                                {Object.values(tabs).map(({ content }, idx) => (
                                    <Tab.Panel key={idx}>{content}</Tab.Panel>
                                ))}
                            </Tab.Panels>
                        </Tab.Group>
                    </div>
                </Container>
            ) : (
                <Container returnUrl="/payments">
                    <Banner kind="error" onClose={() => null} stretch="parent">
                        <>{paymentError ?? 'Payment not found'}</>
                    </Banner>
                </Container>
            )}
        </>
    )
}

export default PaymentDetailPage
